import { t } from "i18next";
import { isNumber } from "lodash";

import { API_BASE, errors } from "../constants";
import {
  BookingDto,
  createErrorResult,
  createSuccessResult,
  IBookingPrepareResult,
  IBulkPrepareBookingPayload,
  IPassengerGetStationsDto,
  ISearchPassengerStations,
  SimpleResult,
  type IBulkCloneBookingsDto,
} from "../dto";
import {
  Api,
  getBookingBulkArchivePath,
  getBookingBulkCancelPath,
  getBookingBulkClonePath,
  getCompanyId,
  Log,
} from "../helpers";
import {
  BookingEntity,
  IBookingsPollingAbortPayload,
  IBookingsPollingAbortResult,
  IBookingsPollingCheckResult,
  IBookingsPollingInitResult,
  IBookingValidateErrors,
  IBulkBookingsCheckStatusPayload,
  IBulkSetBookingsInitPayload,
  IBulkUpdateBookingsDateTimeInitPayload,
  IRydeProcess,
  isBookingEntity,
  isBookingErrors,
  ISearchCriteria,
  PassengerEntity,
} from "../models";
import { apiPatch } from "./generic-api";

export class BookingApi {
  static async update(
    bookingId: number,
    data: unknown,
  ): Promise<SimpleResult<BookingEntity | IBookingValidateErrors>> {
    const companyId = getCompanyId();

    if (typeof data !== "object") {
      Log.error(
        t("errors.wrongDataTypeExpectedObject", { dataType: typeof data }),
      );
      return createErrorResult("errors.wrongDataTypeExpectedObject");
    }

    let result: SimpleResult<BookingEntity | IBookingValidateErrors>;

    try {
      const updateResult = await apiPatch<BookingEntity>(
        `${API_BASE}/c/${companyId}/bookings/${bookingId}`,
        {
          body: { ...data },
        },
      );

      if (
        updateResult &&
        isBookingEntity(updateResult as BookingEntity | IBookingValidateErrors)
      ) {
        result = createSuccessResult(updateResult);
        Log.infoAndToast(t("booking.BOOKING_UPDATED"));
      } else if (isBookingErrors(updateResult)) {
        result = new SimpleResult({
          success: false,
          error: updateResult.errors.at(0),
        });
        Log.errorAndToast(t(`booking.bulk.${updateResult.errors.at(0)}`));
      } else {
        Log.errorAndToast(t("errors.errorOccurredWhenUpdatingBooking"));
        result = createErrorResult("errors.wrongDataTypeExpectedObject");
      }
    } catch (error) {
      Log.errorAndToast(t("errors.errorOccurredWhenUpdatingBooking"), error);
      result = createErrorResult("errors.wrongDataTypeExpectedObject");
    }

    return result;
  }

  static async count(payload: ISearchCriteria<BookingDto>): Promise<number> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/bookings/count`;
    const response = await Api.post<boolean>(requestUrl, payload);

    if (isNumber(response?.data)) {
      return response?.data;
    }

    Log.error(
      t(errors.booking.wrongResponseTypeFromBookingCount, {
        dataType: typeof response?.data,
      }),
      response?.data,
    );

    return 0;
  }

  static async bookingsCalendarPassengersCount(
    payload: ISearchCriteria<PassengerEntity>,
  ): Promise<number> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/passengers/count`;
    const response = await Api.post<boolean>(requestUrl, payload);

    if (isNumber(response?.data)) {
      return response?.data;
    }

    Log.error(
      t("errors.wrongResponseTypeFromPassengersBookingCalendarCount", {
        dataType: typeof response?.data,
      }),
      response?.data,
    );

    return 0;
  }

  static async searchPassengerStations(
    payload: ISearchPassengerStations,
  ): Promise<SimpleResult<IPassengerGetStationsDto[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/bookings/search-passenger-stations`;
    const response = await Api.post<SimpleResult<IPassengerGetStationsDto[]>>(
      requestUrl,
      payload,
    );
    return response?.data;
  }

  static async bulkArchive(
    ids: number[],
  ): Promise<SimpleResult<IRydeProcess> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = getBookingBulkArchivePath(companyId || 0);
    const response = await Api.delete<SimpleResult<IRydeProcess>>(requestUrl, {
      ids,
    });

    return response?.data;
  }

  static async bulkCancel(
    ids: number[],
  ): Promise<SimpleResult<IRydeProcess> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = getBookingBulkCancelPath(companyId || 0);
    const response = await Api.patch<SimpleResult<IRydeProcess>>(requestUrl, {
      ids,
    });

    return response?.data;
  }

  static async bulkClone(
    payload: IBulkCloneBookingsDto,
  ): Promise<SimpleResult<IRydeProcess> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = getBookingBulkClonePath(companyId || 0);
    const response = await Api.post<SimpleResult<IRydeProcess>>(
      requestUrl,
      payload,
    );

    return response?.data;
  }

  static async bookingsPollingStart({
    body,
    abortController,
  }: {
    body: IBulkSetBookingsInitPayload;
    abortController?: AbortController;
  }): Promise<SimpleResult<IBookingsPollingInitResult> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/bookings/bulk-polling/init`;
    const response = await Api.postExtended<
      SimpleResult<IBookingsPollingInitResult>
    >({ url, body, abortController });
    return response?.data;
  }

  static async bookingsDateTimeUpdatePollingStart({
    body,
    abortController,
  }: {
    body: IBulkUpdateBookingsDateTimeInitPayload;
    abortController?: AbortController;
  }): Promise<SimpleResult<IBookingsPollingInitResult> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/bookings/bulk-polling/init-date-time-update`;
    const response = await Api.postExtended<
      SimpleResult<IBookingsPollingInitResult>
    >({ url, body, abortController });
    return response?.data;
  }

  static async bookingsPollingCheckStatus({
    body,
    abortController,
  }: {
    body: IBulkBookingsCheckStatusPayload;
    abortController?: AbortController;
  }): Promise<SimpleResult<IBookingsPollingCheckResult> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/bookings/bulk-polling/check-status`;
    const response = await Api.postExtended<
      SimpleResult<IBookingsPollingCheckResult>
    >({ url, body, abortController });
    return response?.data;
  }

  static async bookingsPollingAbort({
    body,
    abortController,
  }: {
    body: IBookingsPollingAbortPayload;
    abortController?: AbortController;
  }): Promise<SimpleResult<IBookingsPollingAbortPayload> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/bookings/bulk-polling/abort`;
    const response = await Api.postExtended<
      SimpleResult<IBookingsPollingAbortResult>
    >({ url, body, abortController });
    return response?.data;
  }

  static async bookingsPrepare({
    body,
    abortController,
  }: {
    body: IBulkPrepareBookingPayload;
    abortController?: AbortController;
  }): Promise<SimpleResult<IBookingPrepareResult> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/bookings/prepare-passengers`;
    const response = await Api.postExtended<
      SimpleResult<IBookingPrepareResult>
    >({ url, body, abortController });
    return response?.data;
  }
}
